import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Sun, Loader2 } from "lucide-react";

const BG_IMG =
  "https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc4MzI2MDEzOXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@solarcrm.com");
  const [password, setPassword] = useState("Admin@12345");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;
  if (user === undefined) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG_IMG} alt="Solar" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F3A66]/85 via-[#0F3A66]/60 to-[#10B981]/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 backdrop-blur-sm flex items-center justify-center">
              <Sun className="w-6 h-6 text-[#FCD34D]" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold">SunTrack CRM</div>
              <div className="text-xs uppercase tracking-widest text-white/70">for Solar EPC teams</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold leading-tight max-w-md">
              From first sunlight to final handover.
            </h1>
            <p className="text-white/80 mt-4 max-w-md leading-relaxed">
              Track leads, manage proposals, run projects, and get paid — all in one workspace built
              for solar EPC companies.
            </p>
            <div className="mt-8 flex gap-6 text-sm">
              <Stat label="Leads → Deals" v="Pipeline" />
              <Stat label="Design → Handover" v="Execution" />
              <Stat label="Invoicing" v="Cash flow" />
            </div>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} SunTrack CRM</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <h2 className="font-display text-3xl font-semibold text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to your workspace.</p>

          <div className="mt-8">
            <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">
              Email
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              className="input-base"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              className="input-base"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign in
          </button>

          <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600">
            <div className="font-semibold text-slate-700 mb-2">Demo accounts</div>
            <ul className="space-y-1 font-mono">
              <li>admin@solarcrm.com / Admin@12345</li>
              <li>sales@solarcrm.com / Sales@12345</li>
              <li>pm@solarcrm.com / PM@12345</li>
              <li>tech@solarcrm.com / Tech@12345</li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="text-white/60 text-xs uppercase tracking-widest">{label}</div>
      <div className="font-display text-lg font-semibold">{v}</div>
    </div>
  );
}
